import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Heart, Award, Users, BookOpen } from "lucide-react";
import PageHeader from "../../components/common/PageHeader";
import ourstoryImg from "../../assets/ourstory.JPG";
import headerImg from "../../assets/he.png";

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Integrity",
      description:
        "We uphold the highest standards of honesty and ethical behavior.",
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We strive for outstanding achievement in all endeavors.",
    },
    {
      icon: Users,
      title: "Community",
      description: "We foster a supportive and inclusive learning environment.",
    },
    {
      icon: BookOpen,
      title: "Innovation",
      description: "We embrace new ideas and modern teaching methods.",
    },
  ];

  return (
    <>
      <PageHeader
        title="About Stirret Schools"
        subtitle="Raising exceptional leaders through quality education since 2017"
        breadcrumbs={[{ label: "About Us" }]}
        backgroundImage={headerImg}
      />

      {/* Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-primary-50 rounded-2xl p-8 md:p-10"
            >
              <div className="w-14 h-14 bg-primary-600 rounded-xl flex items-center justify-center mb-6">
                <Target className="text-white" size={28} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Our Mission
              </h2>
              <p className="text-gray-600 leading-relaxed">
                To provide a nurturing and intellectually stimulating
                environment where every child is inspired to achieve academic
                excellence, develop strong character, and grow into a confident,
                responsible global citizen.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gold-50 rounded-2xl p-8 md:p-10"
            >
              <div className="w-14 h-14 bg-gold-500 rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-white" size={28} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Our Vision
              </h2>
              <p className="text-gray-600 leading-relaxed">
                To be a leading educational institution in Nigeria, renowned for
                producing well-rounded learners who excel academically, uphold
                strong moral values, and are equipped to lead and thrive in an
                increasingly connected world.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                9+ Years of Educational Excellence
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Founded in 2017, Stirret Schools began with a simple yet
                  profound vision: to create an environment where young minds
                  are nurtured and developed into exceptional leaders.
                </p>
                <p>
                  What started as a small Montessori school has grown into a
                  comprehensive educational institution comprising Stirret
                  Montessori Nursery & Primary School and Stirret College,
                  proudly serving students from age 2 through secondary school.
                </p>
                <p>
                  Throughout our journey, we have remained committed to
                  delivering quality education that blends academic excellence
                  with strong character development, preparing our students to
                  thrive and excel in an ever-changing world.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={ourstoryImg}
                alt="Stirret Schools - Our Story"
                className="w-full h-[500px] object-cover rounded-2xl shadow-hard"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary-600 text-white p-6 rounded-2xl shadow-hard">
                <div className="text-4xl font-bold">2017</div>
                <div className="text-sm text-primary-200">Established</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Guiding Principles
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our core values shape everything we do and guide us in our mission
              to raise exceptional leaders.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-6"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-primary-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
